import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta, { getOrganizationSchema } from '@/components/feature/SEOMeta';
import { aboutInfo } from '@/mocks/data';

export default function AboutPage() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [contactFormStatus, setContactFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [contactFormError, setContactFormError] = useState('');

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setContactFormStatus('submitting');
    setContactFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setContactFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !(value as string).trim()) {
          params.append(key, value as string);
        }
      });
      const submitAddr = 'https://readdy.ai/api/form/d9mul0cit35ii3jdlmb0';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setContactFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        if (msg.toLowerCase().includes('spam')) {
          setContactFormStatus('success');
          return;
        }
        setContactFormError(msg);
        setContactFormStatus('error');
      }
    } catch {
      setContactFormError('网络错误，请检查网络连接后重试');
      setContactFormStatus('error');
    }
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 芒果视频 | 免费在线视频平台 品牌介绍"
        description="芒果视频是国内领先的免费在线视频平台，提供海量电影、电视剧、综艺、动漫免费在线观看。了解芒果视频的品牌故事、发展历程和联系方式。"
        keywords="芒果视频关于我们,芒果视频品牌介绍,芒果视频联系方式,芒果视频信誉,免费视频平台"
        canonicalUrl="/about"
        schema={getOrganizationSchema()}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6 py-6 md:py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mt-6" aria-label="品牌介绍">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                关于芒果视频
              </h1>
              <p className="text-sm text-foreground-600 mb-8 max-w-3xl">
                了解芒果视频的品牌故事、核心优势与联系方式——西瓜视频免费和91视频的最佳替代平台
              </p>

              <div className="bg-background-100 rounded-2xl p-6 md:p-8 mb-8">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {aboutInfo.brand.description}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
                  <div className="bg-background-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">我们的使命</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.brand.mission}</p>
                  </div>
                  <div className="bg-background-50 rounded-xl p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.brand.vision}</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5 mb-10 md:mb-14">
                {aboutInfo.stats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-xl p-4 md:p-5 text-center">
                    <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100 text-primary-600">
                      <i className={`${stat.icon} text-lg md:text-xl`}></i>
                    </span>
                    <p className="font-heading text-xl md:text-2xl text-foreground-950 mb-1">{stat.value}</p>
                    <p className="text-xs text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10 md:mb-14" aria-label="核心价值观">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">核心价值观</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                {aboutInfo.brand.values.map((value) => (
                  <div key={value} className="bg-background-100 rounded-xl p-4 text-center">
                    <span className="font-heading text-primary-500 text-lg">{value}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10 md:mb-14" aria-label="用户评价">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">用户好评</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {aboutInfo.testimonials.map((t, i) => (
                  <div
                    key={t.id}
                    className="bg-background-100 rounded-xl p-5 md:p-6 hover:bg-background-200/70 transition-colors duration-200 cursor-pointer"
                    onMouseEnter={() => setActiveTestimonial(i)}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <p className="text-sm font-semibold text-foreground-950">{t.name}</p>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                    <div className="flex items-center gap-0.5 mt-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-yellow-400 text-xs"></i>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-10 md:mb-14" aria-label="联系方式">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">联系我们</h2>
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="lg:w-1/2">
                  <div className="bg-background-100 rounded-xl p-5 md:p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-phone-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">客服电话</p>
                        <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-wechat-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">微信公众号</p>
                        <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.wechat}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-map-pin-line text-lg"></i>
                      </span>
                      <div>
                        <p className="text-xs text-foreground-500">公司地址</p>
                        <p className="text-sm font-medium text-foreground-950">{aboutInfo.contact.address}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2">
                  <div className="bg-background-100 rounded-xl p-5 md:p-6">
                    <h3 className="font-heading text-lg text-foreground-950 mb-4">给我们留言</h3>
                    <form onSubmit={handleContactSubmit} data-readdy-form="">
                      <input type="text" name="_form_id" value="d9mul0cit35ii3jdlmb0" readOnly className="hidden" aria-hidden="true" />

                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm text-foreground-700 mb-1">姓名</label>
                          <input
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-foreground-700 mb-1">邮箱</label>
                          <input
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-foreground-700 mb-1">留言内容</label>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                            placeholder="请输入您的留言（最多500字）"
                          />
                          <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                        </div>

                        <div className="visually-hidden-field">
                          <label htmlFor="phone_alt">备用电话</label>
                          <input type="text" id="phone_alt" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                        </div>

                        <button
                          type="submit"
                          disabled={contactFormStatus === 'submitting'}
                          className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white py-2.5 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                        >
                          {contactFormStatus === 'submitting' ? '提交中...' : '提交留言'}
                        </button>

                        {contactFormStatus === 'success' && (
                          <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                        )}
                        {contactFormStatus === 'error' && contactFormError && (
                          <p className="text-sm text-red-500 text-center">{contactFormError}</p>
                        )}
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8" aria-label="常见问题">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-5">常见咨询</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { q: '芒果视频真的免费吗？', a: '是的，芒果视频对所有用户完全免费。我们通过广告合作维持运营，用户无需支付任何费用即可观看所有内容。' },
                  { q: '需要注册账号吗？', a: '不注册也可以直接观看，注册后可享受收藏、历史记录等更多功能。' },
                  { q: '支持哪些设备？', a: '支持电脑、手机、平板等所有设备，通过浏览器即可访问。' },
                  { q: '画质怎么样？', a: '提供360P至1080P多种清晰度，部分内容支持4K超清画质。' },
                ].map((item, i) => (
                  <div key={i} className="bg-background-100 rounded-xl p-4 md:p-5">
                    <h3 className="text-sm font-semibold text-foreground-950 mb-2">{item.q}</h3>
                    <p className="text-sm text-foreground-600">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}